import { useState, useCallback, useEffect } from 'react';
import { AppVersionInfo, MessagingConfig, MondayIntegrationConfig, MondayWebhookLog } from '../types';
import { getPostgresApiBaseUrl } from '../services/postgresClient';
import { loadSetting } from '../services/rhDataService';
import {
  DEFAULT_APP_VERSION_INFO,
  DEFAULT_MESSAGING_CONFIG,
  DEFAULT_MONDAY_CONFIG,
} from './appDefaults';

export interface UseIntegrationSettingsParams {
  hasSettingsPermission: boolean;
  getSB: () => ReturnType<typeof import('../services/postgresClient').getPostgresClient>;
  postgresApiUrl: string;
  persistSettingIfAuthenticated: (key: string, value: unknown, companyId?: string) => Promise<void>;
  canPersistSettings: () => Promise<{ ok: boolean; sb: ReturnType<UseIntegrationSettingsParams['getSB']> | null }>;
  addNotification: (title: string, message: string, type: 'add' | 'delete' | 'info') => void;
}

/**
 * Configurações globais de integrações: WhatsApp, Monday.com e versão do app.
 * @param params - Permissões e callbacks de notificação
 * @returns Estado das integrações e ações de persistência
 */
export function useIntegrationSettings({
  hasSettingsPermission,
  getSB,
  postgresApiUrl,
  persistSettingIfAuthenticated,
  canPersistSettings,
  addNotification,
}: UseIntegrationSettingsParams) {
  const [messagingConfig, setMessagingConfig] = useState<MessagingConfig>(DEFAULT_MESSAGING_CONFIG);
  const [mondayConfig, setMondayConfig] = useState<MondayIntegrationConfig>(DEFAULT_MONDAY_CONFIG);
  const [mondayLogs, setMondayLogs] = useState<MondayWebhookLog[]>([]);
  const [appVersionInfo, setAppVersionInfo] = useState<AppVersionInfo>(DEFAULT_APP_VERSION_INFO);
  const [isSavingAppVersion, setIsSavingAppVersion] = useState(false);

  const refreshMondayLogs = useCallback(async () => {
    if (!hasSettingsPermission) return;
    try {
      const sb = getSB();
      const { data: { session } } = await sb.auth.getSession();
      if (!session?.user) {
        addNotification('Sessão', 'Faça login para carregar os logs do Monday.', 'info');
        return;
      }
      const base = getPostgresApiBaseUrl(postgresApiUrl);
      const url = base ? `${base}/integrations/monday/logs` : '/integrations/monday/logs';
      const res = await fetch(url, {
        credentials: 'include',
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        addNotification(
          'Logs Monday',
          typeof errBody?.error === 'string' ? errBody.error : 'Falha ao carregar logs.',
          'delete',
        );
        return;
      }
      const body = await res.json();
      setMondayLogs(Array.isArray(body.logs) ? body.logs : []);
    } catch (err) {
      console.warn('[settings] Falha ao carregar monday_webhook_logs', err);
      addNotification('Logs Monday', 'Erro ao buscar logs do servidor.', 'delete');
    }
  }, [hasSettingsPermission, getSB, postgresApiUrl, addNotification]);

  const saveAppVersionInfo = useCallback(async () => {
    setIsSavingAppVersion(true);
    try {
      const { ok, sb } = await canPersistSettings();
      if (!ok || !sb) {
        addNotification('Erro', 'Não foi possível salvar: sessão inválida.', 'info');
        return;
      }
      await persistSettingIfAuthenticated('app_version_info', appVersionInfo);
      addNotification(
        'Versão salva',
        'As informações foram gravadas e aplicadas para todas as empresas.',
        'info',
      );
    } catch (err) {
      console.warn('[settings] Falha ao salvar app_version_info', err);
      addNotification('Erro ao salvar', 'Não foi possível gravar a versão no banco.', 'delete');
    } finally {
      setIsSavingAppVersion(false);
    }
  }, [appVersionInfo, canPersistSettings, persistSettingIfAuthenticated, addNotification]);

  const resetIntegrationState = useCallback(() => {
    setMessagingConfig(DEFAULT_MESSAGING_CONFIG);
    setMondayConfig(DEFAULT_MONDAY_CONFIG);
    setMondayLogs([]);
    setAppVersionInfo(DEFAULT_APP_VERSION_INFO);
  }, []);

  /** Carrega configs de integração a partir do banco após login. */
  const loadIntegrationSettingsFromDb = useCallback(
    async (sb: ReturnType<typeof getSB>) => {
      const msgCfg = await loadSetting<MessagingConfig>(sb, 'messaging_config');
      if (msgCfg) setMessagingConfig({ ...DEFAULT_MESSAGING_CONFIG, ...msgCfg });

      const monCfg = await loadSetting<Record<string, unknown>>(sb, 'monday_integration_config');
      if (monCfg) {
        setMondayConfig({
          ...DEFAULT_MONDAY_CONFIG,
          ...monCfg,
          triggerStatusColumnName:
            (monCfg.triggerStatusColumnName as string) ??
            (monCfg.triggerStatusColumnId as string) ??
            DEFAULT_MONDAY_CONFIG.triggerStatusColumnName,
          mondayColumnNameTitle:
            (monCfg.mondayColumnNameTitle as string) ??
            (monCfg.mondayColumnName as string) ??
            DEFAULT_MONDAY_CONFIG.mondayColumnNameTitle,
          mondayColumnCompanyTitle:
            (monCfg.mondayColumnCompanyTitle as string) ??
            (monCfg.mondayColumnCompany as string) ??
            DEFAULT_MONDAY_CONFIG.mondayColumnCompanyTitle,
          mondayColumnRoleTitle:
            (monCfg.mondayColumnRoleTitle as string) ??
            (monCfg.mondayColumnRole as string) ??
            DEFAULT_MONDAY_CONFIG.mondayColumnRoleTitle,
          mondayColumnCodeTitle:
            (monCfg.mondayColumnCodeTitle as string) ??
            (monCfg.mondayColumnCode as string) ??
            DEFAULT_MONDAY_CONFIG.mondayColumnCodeTitle,
          mondayColumnBirthDateTitle:
            (monCfg.mondayColumnBirthDateTitle as string) ??
            (monCfg.mondayColumnBirthDate as string) ??
            DEFAULT_MONDAY_CONFIG.mondayColumnBirthDateTitle,
          mondayColumnWhatsappTitle:
            (monCfg.mondayColumnWhatsappTitle as string) ??
            (monCfg.mondayColumnWhatsapp as string) ??
            DEFAULT_MONDAY_CONFIG.mondayColumnWhatsappTitle,
        });
      }

      await refreshMondayLogs();
    },
    [refreshMondayLogs],
  );

  const loadAppVersionFromDb = useCallback(async (sb: ReturnType<typeof getSB>) => {
    const verInfo = await loadSetting<AppVersionInfo>(sb, 'app_version_info');
    if (verInfo) setAppVersionInfo({ ...DEFAULT_APP_VERSION_INFO, ...verInfo });
  }, []);

  useEffect(() => {
    if (!hasSettingsPermission) return;
    void persistSettingIfAuthenticated('messaging_config', messagingConfig);
  }, [messagingConfig, persistSettingIfAuthenticated, hasSettingsPermission]);

  useEffect(() => {
    if (!hasSettingsPermission) return;
    void persistSettingIfAuthenticated('monday_integration_config', mondayConfig);
  }, [mondayConfig, persistSettingIfAuthenticated, hasSettingsPermission]);

  return {
    messagingConfig,
    setMessagingConfig,
    mondayConfig,
    setMondayConfig,
    mondayLogs,
    appVersionInfo,
    setAppVersionInfo,
    isSavingAppVersion,
    refreshMondayLogs,
    saveAppVersionInfo,
    resetIntegrationState,
    loadIntegrationSettingsFromDb,
    loadAppVersionFromDb,
  };
}
